import {
  evaluatePharmacyRouteAccess,
  type Permission,
  type Role,
} from "@medicorepay/shared";
import { useEffect, useRef } from "react";
import toast from "react-hot-toast";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { usePermissions } from "../../hooks/usePermissions";
import { useAuthStore } from "../../stores/authStore";

export function PharmacyRouteGuard() {
  const location = useLocation();
  const user = useAuthStore((state) => state.user);
  const { hasPermission, hasRole, roleName } = usePermissions();
  const shownForPath = useRef<string | null>(null);
  const enabledCapabilities = user?.enabledModules;

  const access = evaluatePharmacyRouteAccess(location.pathname, {
    enabledCapabilities: enabledCapabilities ?? [],
    roleName,
    hasPermission: (permission: Permission) => hasPermission(permission),
    hasRole: (role: Role) => hasRole(role),
    pageOverrides: user?.pageOverrides,
    disabledPageKeys: user?.disabledPageKeys,
  });

  useEffect(() => {
    if (
      user &&
      enabledCapabilities &&
      !access.allowed &&
      shownForPath.current !== location.pathname
    ) {
      toast.error(
        access.denial === "ROUTE_NOT_RELEASED"
          ? "This route is not available in the pharmacy release."
          : access.denial === "PAGE_DENIED"
            ? "This page is disabled for your tenant."
            : "You do not have access to this pharmacy route.",
        { id: "pharmacy-route-denied" },
      );
      shownForPath.current = location.pathname;
    }
  }, [
    access.allowed,
    access.denial,
    enabledCapabilities,
    location.pathname,
    user,
  ]);

  if (!user || !enabledCapabilities) {
    return (
      <div className="flex h-[60vh] items-center justify-center text-sm text-gray-500">
        Loading access…
      </div>
    );
  }

  if (!access.allowed) {
    return <Navigate to="/dashboard" state={{ from: location }} replace />;
  }

  return <Outlet />;
}
